import React from 'react';
import { Box, StatusIndicator, StatusIndicatorProps, Table } from '@amzn/awsui-components-react';
import { PrerequisiteEntry } from 'src/models/promotion/BulkPromotionTypes';
import { PrerequisiteActionability } from 'src/models/promotion/bulkPromotionEnums';
import { humaniseToken } from './bulkPromotionBands';

const ACTIONABILITY_PRESENTATION: Record<string, [string, StatusIndicatorProps.Type]> = {
    [PrerequisiteActionability.PROMOTABLE]: ['You can promote this', 'info'],
    [PrerequisiteActionability.AWAITING_TARGET_APPROVAL]: ['Awaiting approval in the target', 'pending'],
    [PrerequisiteActionability.NOT_SELECTABLE]: ['Promoted with its parent, not on its own', 'info'],
};

/** Most-unblocking first, so the row an operator should act on is at the top. */
export function sortPrerequisitesByImpact(prerequisites: PrerequisiteEntry[]): PrerequisiteEntry[] {
    return [...prerequisites].sort((a, b) => b.blockedCount - a.blockedCount);
}

/**
 * Prerequisites, one row per distinct missing dependency. The service has already collapsed the
 * per-component blocked rows into these, which is what turns a wall of blocked components into a
 * short list of things to go and do.
 */
export default function PrerequisiteList(props: { prerequisites?: PrerequisiteEntry[] }) {
    /*
     * Defaulted rather than assumed. `prerequisites` is not @required on either wire shape that carries it,
     * and both call sites pass it straight off a payload — so an omitted list arrived as undefined and
     * `prerequisites.length` threw inside render, unmounting the tree and blanking the page for a batch that
     * was promoting normally. Typed optional so the compiler names any future caller with the same gap.
     */
    const prerequisites = props.prerequisites || [];
    if (prerequisites.length === 0) {
        return null;
    }

    const ordered = sortPrerequisitesByImpact(prerequisites);

    return (
        <Table
            variant="embedded"
            trackBy="componentId"
            items={ordered}
            columnDefinitions={[
                {
                    id: 'componentName',
                    header: 'Prerequisite',
                    cell: (item: PrerequisiteEntry) => item.componentName || item.componentId,
                    isRowHeader: true,
                },
                {
                    id: 'componentType',
                    header: 'Type',
                    cell: (item: PrerequisiteEntry) => humaniseToken(item.componentType),
                },
                {
                    id: 'blockedCount',
                    header: 'Components blocked',
                    cell: (item: PrerequisiteEntry) => item.blockedCount,
                },
                {
                    id: 'level',
                    header: 'Level',
                    cell: (item: PrerequisiteEntry) => item.level,
                },
                {
                    id: 'actionability',
                    header: 'What to do',
                    cell: (item: PrerequisiteEntry) => {
                        const [label, band] = ACTIONABILITY_PRESENTATION[item.actionability] || [
                            humaniseToken(item.actionability),
                            'info',
                        ];
                        return <StatusIndicator type={band}>{label}</StatusIndicator>;
                    },
                },
            ]}
            header={
                <Box>
                    <Box variant="h4">Prerequisites ({ordered.length})</Box>
                    <Box variant="small">
                        Promoting a prerequisite does not by itself unblock the components above it — a promotion lands
                        as a draft, so it has to be approved in the target first. A multi-level close is a sequence of
                        promote-then-approve rounds.
                    </Box>
                </Box>
            }
            empty={<Box textAlign="center">No prerequisites.</Box>}
        />
    );
}
